import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Clock, Users, RefreshCw, CheckCircle } from 'lucide-react';

export default function QueueStatus({ sessionId, onStatusChange }) {
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      fetchQueueStatus();
      // Poll every 10 seconds
      const interval = setInterval(fetchQueueStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  const fetchQueueStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}/queue-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueData(response.data);
      
      // Notify parent if status changed
      if (onStatusChange && response.data.status !== 'queued' && response.data.status !== 'pending') {
        onStatusChange(response.data.status);
      }
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (!queueData) return null;

  // Session is no longer in queue
  if (queueData.status === 'active') {
    return (
      <div className="card p-4 bg-green-50 border-green-200" data-testid="queue-status-active">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-600" size={24} />
          </div>
          <div>
            <h4 className="font-medium text-green-800">Session Active</h4>
            <p className="text-sm text-green-600">A counselor has joined your session</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4" data-testid="queue-status">
      {/* Position Indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">#{queueData.queue_position}</span>
          </div>
          <div>
            <h4 className="font-medium text-[#2D3748]">Your Position in Queue</h4>
            <p className="text-sm text-[#718096]">
              {queueData.queue_position === 1 
                ? "You're next!" 
                : `${queueData.queue_position - 1} people ahead of you`}
            </p>
          </div>
        </div>
        <button 
          onClick={fetchQueueStatus}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="refresh-queue"
        >
          <RefreshCw size={18} className="text-[#718096]" />
        </button>
      </div>

      {/* Estimated Wait Time */}
      <div className="flex items-center gap-4 p-3 bg-[#FAF9F6] rounded-xl mb-3">
        <Clock size={20} className="text-primary" />
        <div>
          <div className="text-sm text-[#718096]">Estimated wait time</div>
          <div className="font-semibold text-[#2D3748]">
            {queueData.estimated_wait_minutes <= 1 
              ? 'Less than a minute' 
              : `~${queueData.estimated_wait_minutes} minutes`}
          </div>
        </div>
      </div>

      {/* Available Providers */}
      <div className="flex items-center gap-4 p-3 bg-[#FAF9F6] rounded-xl">
        <Users size={20} className="text-primary" />
        <div>
          <div className="text-sm text-[#718096]">Available counselors</div>
          <div className="font-semibold text-[#2D3748]">
            {queueData.available_providers} online
          </div>
        </div>
      </div>

      {/* Progress Animation */}
      <div className="mt-4">
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary rounded-full animate-pulse"
            style={{ width: `${Math.max(10, 100 - (queueData.queue_position * 15))}%` }}
          ></div>
        </div>
        <p className="text-xs text-center text-[#A0AEC0] mt-2">
          Please stay on this page. We'll connect you shortly.
        </p>
      </div>
    </div>
  );
}
